import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';

const CommercialDueDiligence = () => {
  usePageTitle('Commercial Due Diligence');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Commercial Due Diligence</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/cddImage1.png' 
            alt="Commercial Due Diligence" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              While financial and operational due diligence focuses on the internal health of a company, 
              Commercial Due Diligence (CDD) assesses the business’s external environment and market positioning.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, our CDD evaluates the target's revenue model, industry competitiveness, 
              customer behavior, and future growth viability — offering a strategic lens on whether the business 
              is built for long-term success.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              In an age of dynamic markets, shifting regulations, and changing consumer expectations, 
              CDD gives investors and acquirers the clarity they need to make market-savvy, future-ready decisions.
            </p>
          </Card>
        </div>

        {/* What CDD Covers Section */}
        <div className="col-12 mt-5">
          <h2>What Commercial Due Diligence Covers</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Our CDD helps answer strategic questions such as:
              </p>
              <ul className="text-xl line-height-3 ml-4">
                <li className="mb-2">Is the business model sustainable in its market?</li>
                <li className="mb-2">Are growth projections realistic or overly optimistic?</li>
                <li className="mb-2">How does the company stack up against competitors?</li>
                <li className="mb-2">Are customers satisfied and likely to stay loyal?</li>
                <li>What external risks can disrupt revenue and profitability?</li>
              </ul>
            </div>
            <img 
              src='/images/cddImage2.png' 
              alt="Commercial Analysis" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '400px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* CDD Process Section */}
        <div className="col-12 mt-5">
          <h2>Our Commercial Due Diligence Process</h2>
          <p className="text-xl mb-4">
            SnP Finvisors deploys a mix of industry research, market data analytics, customer feedback, 
            and competitor benchmarking to deliver actionable insights.
          </p>
          <Divider />
          
          <div className="grid">
            {/* Process Item 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Market Analysis & Industry Positioning" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of total addressable market (TAM), growth trends, and maturity stage.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of market share, brand strength, and distribution channels.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Industry regulations, entry barriers, and innovation landscape.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. Competitive Landscape Assessment" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of direct, indirect, and emerging competitors.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Benchmarking product/service offerings, pricing, and customer satisfaction.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of competitive threats and strategic differentiators.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. Customer Segmentation & Demand Drivers" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Breakdown of customer base by size, geography, industry, or profile.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of customer acquisition channels and retention metrics.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Study of customer trends, seasonality, and loyalty indicators.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Revenue Quality & Pricing Strategy" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of revenue streams (recurring vs. one-time).</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of pricing power, discount policies, and cost pass-through capability.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of client concentration risk and contract stickiness.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Business Model & Strategic Fit" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Study of unit economics, value proposition, and business scalability.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Alignment with acquirer/investor goals or group synergies.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Future-readiness in terms of ESG, digital adoption, and innovation.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 6 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="6. Risk Factors & External Dependencies" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Economic, geopolitical, or supply chain risks.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Regulatory compliance landscape and upcoming policy changes.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Reputation risks, customer churn, and demand substitution.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 7 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="7. Growth Projections & Sensitivity Analysis" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of internal projections vs. market trends.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Stress testing of assumptions (e.g., customer growth, pricing, margins).</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Scenario modeling and downside risk estimation.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 8 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="8. Primary Research (if required)" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Customer interviews, surveys, or focus group feedback.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Vendor discussions to understand operational dependencies.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Expert insights from industry veterans and analysts.</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <h2>Strategic Insights for Market Success</h2>
              <Divider />
              <p className="text-xl line-height-3">
                At SnP Finvisors, our Commercial Due Diligence provides a comprehensive view of a business’s 
                market position and growth potential. We deliver the insights you need to navigate competitive 
                landscapes, mitigate external risks, and capitalize on market opportunities.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold">
                Because sustainable success depends not just on internal operations, but on thriving in the 
                external market environment.
              </p>
            </div>
            <img 
              src='/images/cddImage3.png'
              alt="Commercial Excellence" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialDueDiligence;